/*
    PaymentsDue: includes ALL of the payment statments
    PaymentToPaymentDue: includes all payments ever made (multiple payments can be made on a single statement)
*/
import { last, reverse, sumBy } from 'lodash';

import { AppPaymentDueFragment, BillingStatus } from '@willow/graphql-iso/src/app';
import { PortalPaymentDueFragment, PortalPaymentFragment } from '@willow/graphql-iso/src/portal';

import { paymentDateFormat } from './paymentFormat';

export function orderPaymentsDue(paymentsDue: PortalPaymentDueFragment[]): PortalPaymentDueFragment[];
export function orderPaymentsDue(paymentsDue: AppPaymentDueFragment[]): AppPaymentDueFragment[];
export function orderPaymentsDue(
  paymentsDue: PortalPaymentDueFragment[] | AppPaymentDueFragment[],
): PortalPaymentDueFragment[] | AppPaymentDueFragment[] {
  // The most recent payment due is last in the array
  return reverse([...paymentsDue]);
}

export const getNextPaymentDue = (
  paymentsDue: PortalPaymentDueFragment[] | AppPaymentDueFragment[],
): PortalPaymentDueFragment | AppPaymentDueFragment | undefined => {
  // Get the next statement date by taking the last paymentDue item
  return last(paymentsDue.filter((pd) => !pd.status.workoutPlanType));
};

export interface GetUnfulfilledPaymentsDueOptions {
  /** If provided, only return payments whose billingStatus is in this list. Omit to include all statuses. */
  billingStatuses?: BillingStatus[];
}

export function getUnfulfilledPaymentsDue(
  paymentsDue: PortalPaymentDueFragment[],
  options?: GetUnfulfilledPaymentsDueOptions,
): PortalPaymentDueFragment[];
export function getUnfulfilledPaymentsDue(
  paymentsDue: AppPaymentDueFragment[],
  options?: GetUnfulfilledPaymentsDueOptions,
): AppPaymentDueFragment[];
export function getUnfulfilledPaymentsDue(
  paymentsDue: PortalPaymentDueFragment[] | AppPaymentDueFragment[],
  options?: GetUnfulfilledPaymentsDueOptions,
): PortalPaymentDueFragment[] | AppPaymentDueFragment[] {
  // The paymentsDue array contains a history of all statements
  // To get the current statements, filter amountRemaining > 0 to see if anything is owed to the lender
  const unfulfilledPaymentsDue = paymentsDue.filter(
    (paymentDue) =>
      paymentDue.status.amountRemainingFromBorrower > 0 &&
      (options?.billingStatuses ? options.billingStatuses.includes(paymentDue.status.billingStatus) : true),
  );
  return orderPaymentsDue(unfulfilledPaymentsDue) || [];
}

export function getOldestUnfulfilledPaymentDue(paymentsDue: PortalPaymentDueFragment[]): PortalPaymentDueFragment;
export function getOldestUnfulfilledPaymentDue(paymentsDue: AppPaymentDueFragment[]): AppPaymentDueFragment;
export function getOldestUnfulfilledPaymentDue(
  paymentsDue: PortalPaymentDueFragment[] | AppPaymentDueFragment[],
): PortalPaymentDueFragment | AppPaymentDueFragment | undefined {
  const unfulfilledPaymentsDue = paymentsDue.filter((paymentDue) => paymentDue.status.amountRemainingFromBorrower > 0);
  return unfulfilledPaymentsDue[0];
}

export const getPaymentsProcessingTotal = (paymentsProcessing: PortalPaymentFragment[]): number => {
  return sumBy(paymentsProcessing, (payment) => payment.total);
};

export const getNextPaymentDueDate = (
  paymentsDue: PortalPaymentDueFragment[] | AppPaymentDueFragment[],
): string | undefined => {
  const nextPaymentDue = getNextPaymentDue(paymentsDue);
  return paymentDateFormat(nextPaymentDue?.paymentDate);
};

export const getOldestUnfulfilledPaymentDueDate = (
  paymentsDue: PortalPaymentDueFragment[] | AppPaymentDueFragment[],
): string | undefined => {
  const paymentDue = getOldestUnfulfilledPaymentDue(paymentsDue);
  return paymentDateFormat(paymentDue?.paymentDate);
};

// Whether a statement should be shown to the current servicer. Some months are generated with a zeroed
// balance we don't expect to collect, and after a loan transfers the on/after-transfer statements
// belong to the next servicer (also zeroed) — both are hidden.
export const isDisplayablePaymentDue = (
  paymentDue: Pick<AppPaymentDueFragment | PortalPaymentDueFragment, 'status' | 'paymentDate'>,
  transferDate: Date | undefined,
  interestDueAtPayoff: boolean,
): boolean =>
  Boolean(
    paymentDue.status.workoutPlanType ||
    paymentDue.status.fulfilled ||
    (!paymentDue.status.fulfilled &&
      (paymentDue.status.amountRemainingFromBorrower > 0 || paymentDue.status.amountRemainingFromCredit > 0)) ||
    // An interest-due-at-payoff statement bills $0, so both remaining amounts read 0 and the check above
    // calls it uncollectable, though the accrued interest is still owed
    (interestDueAtPayoff && paymentDue.status.totalAmountRemaining > 0) ||
    (transferDate && new Date(paymentDue.paymentDate).getTime() < transferDate.getTime()),
  );

export const getDisplayablePaymentsDue = <T extends PortalPaymentDueFragment | AppPaymentDueFragment>(
  paymentsDue: T[],
  transferDate: Date | undefined,
  interestDueAtPayoff: boolean,
): T[] => paymentsDue.filter((paymentDue) => isDisplayablePaymentDue(paymentDue, transferDate, interestDueAtPayoff));
